import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
  Table,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/plugins/monthSelect/style.css";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { requestJson } from "../../../helpers/api";
import baconLogo from "../../../assets/images/baconappen.png";
import { resolveUsuarioActual } from "./ControlStock/formatters";
import { createMonthSelectPlugin } from "./ControlStock/dateUtils";

type ReposicionFiltrosResponse = {
  servidores?: string[];
  sucursales?: string[];
};

type ReposicionResumenApiRow = {
  idArticulo: string | null;
  articulo: string | null;
  inicial: number;
  teorico: number;
  consumo: number;
  merma: number;
  final: number;
  pct30A: number;
  pct30B: number;
  pct40: number;
  total: number;
};

type InventarioInicialControlRow = {
  idMovimiento: number | null;
  sucursal: string | null;
  fechaCarga: string | null;
  idArticulo: string | null;
  articulo: string | null;
  cantidad: number;
};

type IdmovInicialOption = {
  id: string;
  sucursal: string;
  fechaCarga: string | null;
};

type ReposicionRow = {
  idArticulo: string;
  articulo: string;
  inicial: number;
  teorico: number;
  consumo: number;
  merma: number;
  final: number;
  pct30A: number;
  pct30B: number;
  pct40: number;
  total: number;
};

const numberFormat = new Intl.NumberFormat("es-PY", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const getFirstDayFromMonth = (monthValue: string) => `${monthValue}-01`;

const parseMonthForPicker = (value?: string | null) => {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) {
    return null;
  }
  const parsed = new Date(`${value}-01T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatPeriodoMes = (value: string) => {
  const parsed = parseMonthForPicker(value);
  if (!parsed) return value || "-";
  const label = new Intl.DateTimeFormat("es-PY", {
    month: "long",
    year: "numeric",
  }).format(parsed);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const normalizeText = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const formatFechaCorta = (value?: string) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return "-";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("es-PY");
};

const splitFechaHora = (value?: string | null) => {
  if (!value) {
    return { fecha: "-", hora: "-" };
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return { fecha: value, hora: "-" };
  }
  return {
    fecha: parsed.toLocaleDateString("es-PY"),
    hora: parsed.toLocaleTimeString("es-PY", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

const StockReposicionOfflinePage = () => {
  const currentMonth = getCurrentMonth();
  const [serverOptions, setServerOptions] = useState<string[]>([]);
  const [serverSeleccionado, setServerSeleccionado] = useState("");
  const [sucursalOptions, setSucursalOptions] = useState<string[]>([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState("");
  const [periodo, setPeriodo] = useState(currentMonth);
  const [fechaInventarioInicial, setFechaInventarioInicial] = useState(
    getFirstDayFromMonth(currentMonth),
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [idmovInicialFilter, setIdmovInicialFilter] = useState("");
  const [idmovInicialOptions, setIdmovInicialOptions] = useState<IdmovInicialOption[]>([]);
  const [idmovInitialOpen, setIdmovInitialOpen] = useState(false);
  const [rows, setRows] = useState<ReposicionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [excelDropdownOpen, setExcelDropdownOpen] = useState(false);
  const [pdfDropdownOpen, setPdfDropdownOpen] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);

  const selectedIdmovOption = useMemo(
    () => idmovInicialOptions.find((item) => item.id === idmovInicialFilter) ?? null,
    [idmovInicialFilter, idmovInicialOptions],
  );

  const idmovToggleText = useMemo(() => {
    if (idmovInicialOptions.length === 0) {
      return "Invent. Inicial: Sin IDMOV";
    }
    if (!selectedIdmovOption) {
      return "Invent. Inicial: Todos (sumar)";
    }
    const { fecha, hora } = splitFechaHora(selectedIdmovOption.fechaCarga);
    return `Inv.: ${selectedIdmovOption.id} · ${selectedIdmovOption.sucursal} · ${fecha} · ${hora}`;
  }, [idmovInicialOptions.length, selectedIdmovOption]);

  useEffect(() => {
    document.title = "Stock - Reposición de stock (Consolidado) | Informes | Saas";
  }, []);

  useEffect(() => {
    if (!serverSeleccionado || !fechaInventarioInicial) {
      setIdmovInicialOptions([]);
      setIdmovInicialFilter("");
      return;
    }

    let active = true;
    const loadIdmovInicialOptions = async () => {
      try {
        const params = new URLSearchParams({
          server: serverSeleccionado,
          fechaInventarioInicial,
        });
        if (sucursalSeleccionada) {
          params.set("sucursal", sucursalSeleccionada);
        }
        const data = await requestJson<InventarioInicialControlRow[]>(
          `/api/informes/stock/reposicion/inventario-inicial?${params.toString()}`,
        );
        if (!active) return;

        const optionsMap = new Map<string, IdmovInicialOption>();
        (Array.isArray(data) ? data : []).forEach((row) => {
          if (
            typeof row?.idMovimiento !== "number" ||
            !Number.isFinite(row.idMovimiento)
          ) {
            return;
          }
          const id = String(row.idMovimiento);
          if (optionsMap.has(id)) {
            return;
          }
          const sucursal =
            String(row.sucursal ?? "SIN SUCURSAL").trim() || "SIN SUCURSAL";
          optionsMap.set(id, {
            id,
            sucursal,
            fechaCarga: row.fechaCarga ?? null,
          });
        });

        const options = Array.from(optionsMap.values()).sort(
          (a, b) => Number(b.id) - Number(a.id),
        );

        setIdmovInicialOptions(options);
        setIdmovInicialFilter((prev) => {
          if (prev && options.some((option) => option.id === prev)) return prev;
          return options[0]?.id ?? "";
        });
      } catch (err) {
        if (!active) return;
        console.error("No se pudieron cargar opciones de IDMOV inicial:", err);
        setIdmovInicialOptions([]);
        setIdmovInicialFilter("");
      }
    };

    void loadIdmovInicialOptions();
    return () => {
      active = false;
    };
  }, [fechaInventarioInicial, serverSeleccionado, sucursalSeleccionada]);

  const loadServers = useCallback(async () => {
    try {
      const response = await requestJson<ReposicionFiltrosResponse>(
        "/api/informes/stock/reposicion/filtros",
      );
      const options = Array.from(
        new Set(
          (response?.servidores ?? [])
            .map((item) => item.trim().toUpperCase())
            .filter((item) => item.length > 0),
        ),
      );
      setServerOptions(options);
      setServerSeleccionado((prev) => {
        if (prev && options.includes(prev)) return prev;
        if (options.includes("PARLA_SERVER")) return "PARLA_SERVER";
        return options[0] ?? "";
      });
    } catch (err) {
      console.error("No se pudieron cargar los servidores:", err);
      setServerOptions([]);
      setServerSeleccionado("");
      setError("No se pudieron cargar los servidores.");
    }
  }, []);

  useEffect(() => {
    void loadServers();
  }, [loadServers]);

  useEffect(() => {
    if (!serverSeleccionado) {
      setSucursalOptions([]);
      setSucursalSeleccionada("");
      return;
    }

    let active = true;
    const loadSucursales = async () => {
      try {
        const params = new URLSearchParams({
          server: serverSeleccionado,
          periodo,
        });
        const data = await requestJson<ReposicionFiltrosResponse>(
          `/api/informes/stock/reposicion/filtros?${params.toString()}`,
        );
        if (!active) return;
        const options = Array.from(
          new Set(
            (data?.sucursales ?? [])
              .map((item) => item.trim())
              .filter((item) => item.length > 0),
          ),
        ).sort((a, b) => a.localeCompare(b, "es"));
        setSucursalOptions(options);
        setSucursalSeleccionada((prev) =>
          prev && options.includes(prev) ? prev : "",
        );
      } catch (err) {
        if (!active) return;
        console.error("No se pudieron cargar sucursales:", err);
        setSucursalOptions([]);
        setSucursalSeleccionada("");
      }
    };

    void loadSucursales();
    return () => {
      active = false;
    };
  }, [periodo, serverSeleccionado]);

  useEffect(() => {
    let active = true;
    const loadLogo = async () => {
      try {
        const sources = [baconLogo, "/baconappen_529x124.png", "/baconappen.png"];
        for (const source of sources) {
          if (!source) continue;
          try {
            const response = await fetch(source);
            if (!response.ok) continue;
            const blob = await response.blob();
            const reader = new FileReader();
            const dataUrl = await new Promise<string>((resolve, reject) => {
              reader.onloadend = () => {
                if (typeof reader.result === "string") {
                  resolve(reader.result);
                } else {
                  reject(new Error("No pudimos leer el logo."));
                }
              };
              reader.onerror = () => reject(new Error("No pudimos leer el logo."));
              reader.readAsDataURL(blob);
            });
            if (active) {
              setLogoDataUrl(dataUrl);
            }
            return;
          } catch {
            continue;
          }
        }
      } catch {
        // noop
      }
    };
    void loadLogo();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!serverSeleccionado || !periodo) {
      setRows([]);
      return;
    }

    let active = true;
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          periodo,
          server: serverSeleccionado,
          fechaInventarioInicial,
        });
        if (sucursalSeleccionada) {
          params.set("sucursal", sucursalSeleccionada);
        }
        if (idmovInicialFilter.trim()) {
          params.set("idmovInicial", idmovInicialFilter.trim());
        }
        const data = await requestJson<ReposicionResumenApiRow[]>(
          `/api/informes/stock/reposicion?${params.toString()}`,
        );
        if (!active) return;

        const normalized = (Array.isArray(data) ? data : [])
          .map((row) => ({
            idArticulo: String(row.idArticulo ?? "SIN ID").trim() || "SIN ID",
            articulo:
              String(row.articulo ?? "Sin descripción").trim() ||
              "Sin descripción",
            inicial: Number(row.inicial ?? 0),
            teorico: Number(row.teorico ?? 0),
            consumo: Number(row.consumo ?? 0),
            merma: Number(row.merma ?? 0),
            final: Number(row.final ?? 0),
            pct30A: Number(row.pct30A ?? 0),
            pct30B: Number(row.pct30B ?? 0),
            pct40: Number(row.pct40 ?? 0),
            total: Number(row.total ?? 0),
          }))
          .sort((a, b) => a.articulo.localeCompare(b.articulo));

        setRows(normalized);
      } catch (err) {
        if (!active) return;
        console.error("No se pudo cargar reposición de stock:", err);
        setError(
          err instanceof Error
            ? err.message
            : "No se pudo cargar la reposición de stock.",
        );
        setRows([]);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void loadData();
    return () => {
      active = false;
    };
  }, [
    fechaInventarioInicial,
    idmovInicialFilter,
    periodo,
    serverSeleccionado,
    sucursalSeleccionada,
  ]);

  const filteredRows = useMemo(() => {
    const query = normalizeText(searchTerm.trim());
    if (!query) return rows;
    return rows.filter((row) => {
      const articleLabel = normalizeText(`${row.idArticulo} ${row.articulo}`);
      return articleLabel.includes(query);
    });
  }, [rows, searchTerm]);

  const totals = useMemo(
    () =>
      filteredRows.reduce(
        (acc, row) => {
          acc.inicial += row.inicial;
          acc.teorico += row.teorico;
          acc.consumo += row.consumo;
          acc.merma += row.merma;
          acc.final += row.final;
          acc.pct30A += row.pct30A;
          acc.pct30B += row.pct30B;
          acc.pct40 += row.pct40;
          acc.total += row.total;
          return acc;
        },
        {
          inicial: 0,
          teorico: 0,
          consumo: 0,
          merma: 0,
          final: 0,
          pct30A: 0,
          pct30B: 0,
          pct40: 0,
          total: 0,
        },
      ),
    [filteredRows],
  );

  const exportDisabled = filteredRows.length === 0;

  const toFileSafe = useCallback((value: string) => {
    const normalized = normalizeText(value)
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return normalized || "stock-reposicion";
  }, []);

  const handleExportExcel = useCallback(() => {
    if (exportDisabled) return;
    const wsRows: Array<Array<string | number>> = [
      [
        "Ingrediente",
        "Inicial",
        "Teórico",
        "Consumo",
        "Merma",
        "Final",
        "30%",
        "30%",
        "40%",
        "Total",
      ],
      ...filteredRows.map((row) => [
        `${row.idArticulo} ${row.articulo}`.trim(),
        Number(row.inicial ?? 0),
        Number(row.teorico ?? 0),
        Number(row.consumo ?? 0),
        Number(row.merma ?? 0),
        Number(row.final ?? 0),
        Number(row.pct30A ?? 0),
        Number(row.pct30B ?? 0),
        Number(row.pct40 ?? 0),
        Number(row.total ?? 0),
      ]),
      [
        "TOTAL",
        Number(totals.inicial ?? 0),
        Number(totals.teorico ?? 0),
        Number(totals.consumo ?? 0),
        Number(totals.merma ?? 0),
        Number(totals.final ?? 0),
        Number(totals.pct30A ?? 0),
        Number(totals.pct30B ?? 0),
        Number(totals.pct40 ?? 0),
        Number(totals.total ?? 0),
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsRows);
    ws["!cols"] = [
      { wch: 46 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 12 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reposicion");
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    XLSX.writeFile(
      wb,
      `stock-reposicion-${toFileSafe(serverSeleccionado || "server")}-${toFileSafe(periodo)}-${timestamp}.xlsx`,
    );
  }, [exportDisabled, filteredRows, periodo, serverSeleccionado, toFileSafe, totals]);

  const handleExportPdf = useCallback(() => {
    if (exportDisabled) return;
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const leftX = 34;
    const rightX = 34;
    const usuario = resolveUsuarioActual();
    const fechaHora = new Date().toLocaleString("es-PY");
    const headerHeight = 90;

    const renderHeader = () => {
      if (logoDataUrl) {
        try {
          doc.addImage(logoDataUrl, "PNG", leftX, 18, 104, 24);
        } catch {
          // ignore logo errors
        }
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text("Stock - Reposición de stock", leftX, 56);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text(`Servidor: ${serverSeleccionado || "-"}`, leftX, 70);
      doc.text(`Período: ${formatPeriodoMes(periodo)}`, leftX + 205, 70);
      doc.text(`Sucursal: ${sucursalSeleccionada || "Todas"}`, leftX + 365, 70);
      doc.text(`Inv. inicial: ${formatFechaCorta(fechaInventarioInicial)}`, leftX + 530, 70);
      doc.text(`Registros: ${filteredRows.length}`, leftX + 690, 70);

      doc.setFontSize(8);
      doc.setTextColor(99, 115, 129);
      const auditoria = `Generado por ${usuario} · ${fechaHora}`;
      doc.text(auditoria, pageWidth - rightX, 28, { align: "right" });
      doc.setTextColor(0, 0, 0);
    };

    renderHeader();

    const body = filteredRows.map((row) => [
      `${row.idArticulo} ${row.articulo}`.trim(),
      numberFormat.format(row.inicial),
      numberFormat.format(row.teorico),
      numberFormat.format(row.consumo),
      numberFormat.format(row.merma),
      numberFormat.format(row.final),
      numberFormat.format(row.pct30A),
      numberFormat.format(row.pct30B),
      numberFormat.format(row.pct40),
      numberFormat.format(row.total),
    ]);
    body.push([
      { content: "TOTAL", styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: numberFormat.format(totals.inicial), styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: numberFormat.format(totals.teorico), styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: numberFormat.format(totals.consumo), styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: numberFormat.format(totals.merma), styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: numberFormat.format(totals.final), styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: numberFormat.format(totals.pct30A), styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: numberFormat.format(totals.pct30B), styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: numberFormat.format(totals.pct40), styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: numberFormat.format(totals.total), styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
    ] as any);

    autoTable(doc, {
      head: [["Ingrediente", "Inicial", "Teórico", "Consumo", "Merma", "Final", "30%", "30%", "40%", "Total"]],
      body,
      startY: headerHeight + 16,
      margin: { left: leftX, right: rightX, top: headerHeight },
      theme: "grid",
      styles: { fontSize: 7, cellPadding: 3 },
      headStyles: { fillColor: [33, 37, 41] },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
        9: { halign: "right" },
      },
      didDrawPage: () => {
        renderHeader();
      },
    });

    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    doc.save(
      `stock-reposicion-${toFileSafe(serverSeleccionado || "server")}-${toFileSafe(periodo)}-${timestamp}.pdf`,
    );
  }, [
    exportDisabled,
    fechaInventarioInicial,
    filteredRows,
    logoDataUrl,
    periodo,
    serverSeleccionado,
    sucursalSeleccionada,
    toFileSafe,
    totals,
  ]);

  return (
    <div className="page-content">
      <Container fluid className="px-2 px-md-3">
        <style>{`
          .reposicion-stock-main-table table th,
          .reposicion-stock-main-table table td {
            vertical-align: top !important;
            padding-top: 0.42rem !important;
            padding-bottom: 0.42rem !important;
            padding-left: 0.55rem !important;
            padding-right: 0.55rem !important;
          }
          .reposicion-stock-main-table table thead th {
            font-size: 0.925rem !important;
            line-height: 1.05 !important;
          }
          .reposicion-stock-main-table table tbody td {
            font-size: 0.925rem !important;
            line-height: 1.06 !important;
          }
          .reposicion-stock-main-table .table-empty-state-cell {
            padding-top: 8rem !important;
            padding-bottom: 8rem !important;
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }
          .reposicion-stock-main-table .reposicion-col-30a {
            background-color: rgba(var(--vz-primary-rgb), 0.08);
          }
          .reposicion-stock-main-table .reposicion-col-30b {
            background-color: rgba(var(--vz-info-rgb), 0.08);
          }
          .reposicion-stock-main-table .reposicion-col-40 {
            background-color: rgba(var(--vz-warning-rgb), 0.12);
          }
          .reposicion-stock-sticky-table thead th {
            position: sticky;
            top: 0;
            z-index: 9;
            background-color: var(--vz-secondary-bg, var(--vz-light));
          }
          .reposicion-stock-sticky-table thead th:first-child {
            left: 0;
            z-index: 11;
            box-shadow: 1px 0 0 var(--vz-border-color);
          }
          .reposicion-stock-sticky-table tbody td:first-child:not([colspan]),
          .reposicion-stock-sticky-table tfoot th:first-child {
            position: sticky;
            left: 0;
            z-index: 6;
            background-color: var(--vz-body-bg);
            box-shadow: 1px 0 0 var(--vz-border-color);
          }
          .reposicion-stock-sticky-table tbody tr.table-light td:first-child:not([colspan]),
          .reposicion-stock-sticky-table tfoot tr.table-light th:first-child {
            background-color: var(--vz-secondary-bg, var(--vz-light));
          }
        `}</style>

        <BreadCrumb title="Stock - Reposición de stock" pageTitle="Informes" />

        <Card className="border-0 shadow-sm">
          <CardHeader className="bg-transparent">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <h4 className="mb-1">Reposición de stock</h4>
                <p className="text-muted mb-0">
                  Base de consumo por receta para planificación de reposición.
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2 ms-auto align-items-center">
                <Dropdown
                  isOpen={excelDropdownOpen}
                  toggle={() => setExcelDropdownOpen((prev) => !prev)}
                >
                  <DropdownToggle
                    color="dark"
                    className="btn-soft-dark"
                    caret
                    disabled={exportDisabled}
                  >
                    <i className="ri-file-excel-2-line me-1"></i>
                    Excel
                  </DropdownToggle>
                  <DropdownMenu end>
                    <DropdownItem onClick={handleExportExcel}>
                      <i className="ri-file-list-3-line me-2"></i>
                      Vista actual
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <Dropdown
                  isOpen={pdfDropdownOpen}
                  toggle={() => setPdfDropdownOpen((prev) => !prev)}
                >
                  <DropdownToggle
                    color="dark"
                    className="btn-soft-dark"
                    caret
                    disabled={exportDisabled}
                  >
                    <i className="ri-file-pdf-2-line me-1"></i>
                    PDF
                  </DropdownToggle>
                  <DropdownMenu end>
                    <DropdownItem onClick={handleExportPdf}>
                      <i className="ri-file-list-3-line me-2"></i>
                      Vista actual
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <Button
                  color="dark"
                  className="btn-soft-dark"
                  onClick={() => setFiltersOpen(true)}
                >
                  <i className="ri-equalizer-line me-1"></i>
                  Filtros
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <div
                style={{ minWidth: 240, maxWidth: 1120 }}
                className="w-100 w-md-auto d-flex align-items-center gap-2"
              >
                <Input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Buscar ingrediente o ID..."
                />
                <div style={{ width: 620 }}>
                  <Dropdown
                    isOpen={idmovInitialOpen}
                    toggle={() => setIdmovInitialOpen((prev) => !prev)}
                  >
                    <DropdownToggle
                      color="outline-secondary"
                      className="w-100 d-flex align-items-center justify-content-between border border-warning bg-transparent text-body"
                      disabled={idmovInicialOptions.length === 0}
                    >
                      <span className="d-block text-truncate" style={{ maxWidth: 560 }}>
                        {idmovToggleText}
                      </span>
                      <i className="ri-arrow-down-s-line ms-2" />
                    </DropdownToggle>
                    <DropdownMenu className="w-100">
                      <DropdownItem onClick={() => setIdmovInicialFilter("")}>
                        Invent. Inicial: Todos (sumar)
                      </DropdownItem>
                      {idmovInicialOptions.map((item) => (
                        <DropdownItem
                          key={item.id}
                          onClick={() => setIdmovInicialFilter(item.id)}
                          active={idmovInicialFilter === item.id}
                          className="text-nowrap"
                        >
                          {(() => {
                            const parts = splitFechaHora(item.fechaCarga);
                            return `Invent. Inicial: ${item.id} · ${item.sucursal} · ${parts.fecha} · ${parts.hora}`;
                          })()}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </div>
            </div>
            <small className="text-muted d-block mb-3">
              Servidor: {serverSeleccionado || "-"} · Período: {formatPeriodoMes(periodo)} ·
              Sucursal: {sucursalSeleccionada || "Todas las sucursales"} ·
              Inv. inicial: {formatFechaCorta(fechaInventarioInicial)} · IDMOV inicial:{" "}
              {idmovInicialFilter
                ? `IDMOV ${idmovInicialFilter}${selectedIdmovOption ? ` · ${selectedIdmovOption.sucursal}` : ""}`
                : "Todos (sumar)"} · Registros:{" "}
              {filteredRows.length}/{rows.length}
            </small>

            {error ? <Alert color="danger">{error}</Alert> : null}
            {loading ? (
              <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                <Spinner color="primary" />
                <small className="mt-2">Cargando...</small>
              </div>
            ) : (
              <div className="table-responsive reposicion-stock-main-table">
                <Table className="table align-top table-striped mb-0 reposicion-stock-sticky-table">
                  <thead className="table-light">
                    <tr>
                      <th>Ingrediente</th>
                      <th className="text-end">Inicial</th>
                      <th className="text-end">Teórico</th>
                      <th className="text-end">Consumo</th>
                      <th className="text-end">Merma</th>
                      <th className="text-end">Final</th>
                      <th className="text-end reposicion-col-30a">30%</th>
                      <th className="text-end reposicion-col-30b">30%</th>
                      <th className="text-end reposicion-col-40">40%</th>
                      <th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-center text-muted table-empty-state-cell">
                          Seleccioná servidor y período para ver resultados.
                        </td>
                      </tr>
                    ) : (
                      filteredRows.map((row) => (
                        <tr key={`${row.idArticulo}-${row.articulo}`}>
                          <td>
                            <div className="d-flex align-items-center gap-2 text-nowrap">
                              <span className="badge bg-primary-subtle text-primary">
                                {row.idArticulo}
                              </span>
                              <span
                                className="fw-semibold text-truncate"
                                style={{ maxWidth: 360 }}
                                title={row.articulo}
                              >
                                {row.articulo}
                              </span>
                            </div>
                          </td>
                          <td className="text-end">{numberFormat.format(row.inicial)}</td>
                          <td className="text-end">{numberFormat.format(row.teorico)}</td>
                          <td className="text-end">{numberFormat.format(row.consumo)}</td>
                          <td className="text-end">{numberFormat.format(row.merma)}</td>
                          <td className="text-end">{numberFormat.format(row.final)}</td>
                          <td className="text-end reposicion-col-30a">{numberFormat.format(row.pct30A)}</td>
                          <td className="text-end reposicion-col-30b">{numberFormat.format(row.pct30B)}</td>
                          <td className="text-end reposicion-col-40">{numberFormat.format(row.pct40)}</td>
                          <td className="text-end fw-semibold">{numberFormat.format(row.total)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="table-light">
                      <th>Total</th>
                      <th className="text-end">{numberFormat.format(totals.inicial)}</th>
                      <th className="text-end">{numberFormat.format(totals.teorico)}</th>
                      <th className="text-end">{numberFormat.format(totals.consumo)}</th>
                      <th className="text-end">{numberFormat.format(totals.merma)}</th>
                      <th className="text-end">{numberFormat.format(totals.final)}</th>
                      <th className="text-end reposicion-col-30a">{numberFormat.format(totals.pct30A)}</th>
                      <th className="text-end reposicion-col-30b">{numberFormat.format(totals.pct30B)}</th>
                      <th className="text-end reposicion-col-40">{numberFormat.format(totals.pct40)}</th>
                      <th className="text-end">{numberFormat.format(totals.total)}</th>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>

      <Offcanvas
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        direction="end"
        className="border-start"
      >
        <OffcanvasHeader toggle={() => setFiltersOpen(false)}>
          Filtros
        </OffcanvasHeader>
        <OffcanvasBody>
          <FormGroup className="mb-3">
            <Label className="form-label mb-1">Servidor</Label>
            <Input
              type="select"
              value={serverSeleccionado}
              onChange={(event) => setServerSeleccionado(event.target.value)}
            >
              {serverOptions.length === 0 ? (
                <option value="">Sin servidores</option>
              ) : null}
              {serverOptions.map((server) => (
                <option key={server} value={server}>
                  {server}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup className="mb-3">
            <Label className="form-label mb-1">Período</Label>
            <Flatpickr
              className="form-control"
              value={parseMonthForPicker(periodo) ? [parseMonthForPicker(periodo)!] : []}
              options={{
                mode: "single",
                altInput: true,
                plugins: [
                  createMonthSelectPlugin({
                    shorthand: true,
                    dateFormat: "Y-m",
                    altFormat: "F Y",
                  }),
                ],
              }}
              onChange={(dates: Date[]) => {
                if (!dates || dates.length === 0 || !dates[0]) {
                  return;
                }
                const selected = dates[0];
                const value = `${selected.getFullYear()}-${String(
                  selected.getMonth() + 1,
                ).padStart(2, "0")}`;
                setPeriodo(value);
                setFechaInventarioInicial(getFirstDayFromMonth(value));
              }}
            />
          </FormGroup>

          <FormGroup className="mb-3">
            <Label className="form-label mb-1">Sucursal</Label>
            <Input
              type="select"
              value={sucursalSeleccionada}
              onChange={(event) => setSucursalSeleccionada(event.target.value)}
            >
              <option value="">Todas las sucursales</option>
              {sucursalOptions.map((sucursal) => (
                <option key={sucursal} value={sucursal}>
                  {sucursal}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup className="mb-3">
            <Label className="form-label mb-1">Fecha inventario inicial</Label>
            <Input
              type="date"
              value={fechaInventarioInicial}
              onChange={(event) => setFechaInventarioInicial(event.target.value)}
            />
          </FormGroup>

          <Button
            color="dark"
            className="btn-soft-dark w-100"
            onClick={() => setFiltersOpen(false)}
          >
            Aplicar
          </Button>
        </OffcanvasBody>
      </Offcanvas>
    </div>
  );
};

export default StockReposicionOfflinePage;
